import React, { forwardRef, useCallback, useState } from 'react';
import styled from 'styled-components';
import * as entities from '../../graphql/entities';
import PermittedLink from './PermittedLink';
import { checkAnyPermission, checkPermission } from '../../utils/OperationPermissions';
import { useFullUserDetails } from '../../utils/hooks';
import { Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import styles from '../../styles/partials/_navbar.css';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import FluentModal from '../modals/FluentModal';

const Title = styled.div`
  font-size: 22px;
  font-weight: 300;
  color: ${p4Tokens.colorNeutralForeground3};
  padding: 10px;
`;

const Options = styled.div`
  width: 160px;
  padding-right: 0;
  padding-left: 10px;
  float: left;
`;

const OptionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${p4Tokens.colorNeutralForeground3};
`;

const NavBarAddDialog = forwardRef<any>(({}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const fullUserDetails = useFullUserDetails();

  const onClose = useCallback(() => setIsOpen(false), []);

  if (!checkAnyPermission({ app: { fullUserDetails } }, ['CUSTOMERS', 'SUPPLIERS', 'PRODUCTS', 'DIARY', 'NOMINAL'])) {
    return (
      <FluentModal isOpen={isOpen} onDismiss={onClose}>
        <Title>User not permitted to add.</Title>
      </FluentModal>
    );
  }

  const customers = (
    <Options>
      <OptionTitle>Customers</OptionTitle>
      <PermittedLink requestClose={onClose} entity={entities.Customer} />
      <PermittedLink requestClose={onClose} entity={entities.SalesOrder} title={'Sales Order'} />
      <PermittedLink
        requestClose={onClose}
        entity={{ ...entities.SalesOrder, formUrl: '/Sales/Order/Template/' }}
        title={'Order Template'}
      />
      <PermittedLink requestClose={onClose} entity={entities.Quotation} />
      <PermittedLink requestClose={onClose} entity={entities.SalesInvoice} />
      <PermittedLink requestClose={onClose} entity={entities.SalesCredit} />
      <PermittedLink requestClose={onClose} entity={entities.SalesReceipt} />
    </Options>
  );
  const suppliers = (
    <Options>
      <OptionTitle>Suppliers</OptionTitle>
      <PermittedLink requestClose={onClose} entity={entities.Supplier} />
      <PermittedLink requestClose={onClose} entity={entities.PurchaseOrder} />
      <PermittedLink requestClose={onClose} entity={entities.GoodsReceipt} />
    </Options>
  );
  const products = (
    <Options>
      <OptionTitle>Products</OptionTitle>
      <PermittedLink requestClose={onClose} entity={entities.Product} />
      <PermittedLink requestClose={onClose} entity={entities.StockAdjustment} />
      <PermittedLink requestClose={onClose} entity={entities.StockTransfer} />
    </Options>
  );
  const other = (
    <Options>
      <OptionTitle>Other</OptionTitle>
      <PermittedLink requestClose={onClose} entity={entities.DiaryEvent} />
      <PermittedLink requestClose={onClose} entity={entities.NominalJournal} />
    </Options>
  );

  return (
    <div ref={ref}>
      <Popover open={isOpen} positioning={'below'} size={'small'} onOpenChange={(e, data) => setIsOpen(data.open)}>
        <PopoverTrigger>
          <div className={styles.addNew}>
            <div style={{ display: 'inline-block' }}>
              <i style={{ fontSize: '24px' }} className="icon">
                add_circle
              </i>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverSurface>
          <Title>Add</Title>
          {checkPermission({ app: { fullUserDetails } }, 'CUSTOMERS') ? customers : null}
          {checkPermission({ app: { fullUserDetails } }, 'SUPPLIERS') ? suppliers : null}
          {checkPermission({ app: { fullUserDetails } }, 'PRODUCTS') ? products : null}
          {checkAnyPermission({ app: { fullUserDetails } }, ['DIARY', 'NOMINAL']) ? other : null}
        </PopoverSurface>
      </Popover>
    </div>
  );
});

export default NavBarAddDialog;
